/** Ground formation members queue through their alliance's gates when a wall
 * blocks their slot. Routes are pure memoized A* results, shared by starting
 * tile; losing this cache on save/load never changes a movement decision. */
import { MAP_W, MAX_UNITS } from '../constants.ts';
import { findPath } from '../path.ts';
import { alliedWith, OB_SOLID, Terrain, UF, WallKind } from '../types.ts';
import { OrderKind, type Company, type World } from '../world.ts';

interface RouteCache {
  map: World['map']; wallVersion: number; terrainVersion: number;
  routes: Map<string, number[]>;
}
const caches = new WeakMap<World, RouteCache>();
const bridgeWalkers = new WeakMap<World, Uint8Array>();

/** Per-step steering intent, derived alongside unit goals and never saved. */
export function beginCrossingRoutes(w: World): void {
  let walkers = bridgeWalkers.get(w);
  if (!walkers) { walkers = new Uint8Array(MAX_UNITS); bridgeWalkers.set(w, walkers); }
  walkers.fill(0, 0, w.units.slots.high);
}
export function staysOnBridge(w: World, i: number): boolean { return bridgeWalkers.get(w)?.[i] === 1; }

function bridgeOnLine(w: World, x0: number, y0: number, x1: number, y1: number): boolean {
  const steps = Math.max(1, Math.ceil(Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0)) * 3));
  for (let n = 0; n <= steps; n++) {
    const x = Math.floor(x0 + (x1 - x0) * n / steps), y = Math.floor(y0 + (y1 - y0) * n / steps);
    if (w.map.inBounds(x, y) && w.map.terrain[w.map.idx(x, y)] === Terrain.Bridge) return true;
  }
  return false;
}

function blocked(w: World, x: number, y: number, team: number, avoidWater = false): boolean {
  const map = w.map;
  if (!map.inBounds(x, y)) return true;
  const tile = map.idx(x, y);
  if (avoidWater && (map.terrain[tile] === Terrain.River || map.terrain[tile] === Terrain.Deep || map.terrain[tile] === Terrain.Ditch)) return true;
  if (map.resourceSolid?.[tile] || (w.obstacleGrid && (w.obstacleGrid[tile] & OB_SOLID))) return true;
  if (map.wall[tile] !== WallKind.None) return map.wall[tile] !== WallKind.Gate || !alliedWith(team, w.wallTeam);
  // A hostile structure is an attackable route cost, but it is still solid
  // when checking whether a formation member can walk a straight segment.
  return map.building[tile] >= 0;
}

/** Centre-line clearance with both sides of diagonal corners checked. */
export function formationLineClear(w: World, x0: number, y0: number, x1: number, y1: number, team: number, avoidWater = false): boolean {
  const dx = x1 - x0, dy = y1 - y0;
  const steps = Math.max(1, Math.ceil(Math.max(Math.abs(dx), Math.abs(dy)) * 4));
  let px = Math.floor(x0), py = Math.floor(y0);
  for (let n = 0; n <= steps; n++) {
    const x = Math.floor(x0 + dx * n / steps), y = Math.floor(y0 + dy * n / steps);
    if (blocked(w, x, y, team, avoidWater)) return false;
    if (x !== px && y !== py && (blocked(w, px, y, team, avoidWater) || blocked(w, x, py, team, avoidWater))) return false;
    px = x; py = y;
  }
  return true;
}

export function crossesWater(w: World, x0: number, y0: number, x1: number, y1: number): boolean {
  const steps = Math.max(1, Math.ceil(Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0)) * 3));
  for (let n = 0; n <= steps; n++) {
    const x = Math.floor(x0 + (x1 - x0) * n / steps), y = Math.floor(y0 + (y1 - y0) * n / steps);
    if (!w.map.inBounds(x, y)) continue;
    const t = w.map.terrain[w.map.idx(x, y)];
    if (t === Terrain.River || t === Terrain.Deep || t === Terrain.Ditch) return true;
  }
  return false;
}

/** Preserve ford rules; require dry ground only when the cheapest route uses
 * a bridge and a continuous route over that crossing actually exists. */
export function findCrossingPath(w: World, sx: number, sy: number, tx: number, ty: number, team: number, out: number[]): number {
  findPath(w.map, sx, sy, tx, ty, team, out, undefined, w.wallTeam);
  if (out.some(tile => w.map.terrain[tile] === Terrain.Bridge)) {
    const dry: number[] = [];
    if (findPath(w.map, sx, sy, tx, ty, team, dry, undefined, w.wallTeam, true) > 0) {
      out.length = 0;
      out.push(...dry);
    }
  }
  return out.length;
}

export function gateTransitTarget(w: World, c: Company, i: number, slotX: number, slotY: number, out: { x: number; y: number }): boolean {
  const u = w.units;
  if ((u.flags[i] & UF.Flying) || u.wallAt[i] !== -1 || u.wallTile[i] >= 0) return false;
  const x = u.x[i], y = u.y[i];
  const water = crossesWater(w, x, y, slotX, slotY);
  // The banner's route does not guarantee clearance for its whole frontage.
  // A line rounding a warehouse needs the same per-member access as a line
  // at a gate, or the near ranks remain pressed against the footprint forever.
  const obstructed = !formationLineClear(w, x, y, slotX, slotY, c.team);
  if (!water && !obstructed) {
    if (bridgeOnLine(w, x, y, slotX, slotY)) bridgeWalkers.get(w)![i] = 1;
    return false;
  }
  let cache = caches.get(w);
  if (!cache || cache.map !== w.map || cache.wallVersion !== w.map.wallVersion || cache.terrainVersion !== w.map.terrainVersion) {
    cache = { map: w.map, wallVersion: w.map.wallVersion, terrainVersion: w.map.terrainVersion, routes: new Map() };
    caches.set(w, cache);
  }
  const sx = Math.floor(x), sy = Math.floor(y);
  const tx = Math.floor(c.orderKind === OrderKind.None ? c.ax : c.orderX);
  const ty = Math.floor(c.orderKind === OrderKind.None ? c.ay : c.orderY);
  const key = `${sy * MAP_W + sx}:${ty * MAP_W + tx}:${c.team}:${w.wallTeam}`;
  let path = cache.routes.get(key);
  if (!path) {
    path = [];
    findCrossingPath(w, sx, sy, tx, ty, c.team, path);
    if (cache.routes.size >= 4096) cache.routes.clear();
    cache.routes.set(key, path);
  }
  // Once a model reaches the far bank its route no longer contains deck
  // tiles, but a lagging formation slot can still point back into the river.
  const bridge = water && path.length > 0 && !path.some(tile => {
    const t = w.map.terrain[tile];
    return t === Terrain.River || t === Terrain.Deep || t === Terrain.Ditch;
  });
  if (water && !bridge && !obstructed) return false;
  let found = false;
  for (const tile of path) {
    const px = tile % MAP_W + 0.5, py = Math.floor(tile / MAP_W) + 0.5;
    if (!formationLineClear(w, x, y, px, py, c.team, bridge)) break;
    out.x = px; out.y = py; found = true;
    if ((px - x) ** 2 + (py - y) ** 2 > 16 ** 2) break;
  }
  if (bridge && found) bridgeWalkers.get(w)![i] = 1;
  return found;
}
